import React, { useState } from 'react';
import { Alert, Button, Card, Form, Icon, Input, notification } from 'antd';
import { useAddSitemapImportProcessMutation, useAddDomainAnalysisProcessMutation } from '../process/service';
import { useFetchWebsiteIndexEntriesQuery, useAddWebsiteIndexEntryMutation } from './service';
import { extractDomainAndHomepage } from '../utils';
import { useNavigate } from "react-router-dom";
import useGetSettingsForPlan from '../../sales/hooks/useGetPlanSettings';
import useGetSubscription from '../../sales/hooks/useGetSubscription';
import { useCreateBackgroundAnalysisReportMutation } from '../../analysis/service';
import './WebsiteIndexEntry.less';


type Props = {
    onFinish: (websiteGuid: string) => void;
};


export default function AddNewWebsiteForm({ onFinish }: Props) {
    const navigate = useNavigate();

    const [inputDomain, setInputDomain] = useState<string>('');
    const [inputDomainError, setInputDomainError] = useState<string>('');
    const [showMaxDomainsError, setShowMaxDomainsError] = useState<boolean>(false);

    const { data: websiteIndexEntries, isLoading: isLoadingWebsiteIndexEntries } = useFetchWebsiteIndexEntriesQuery();
    const [addWebsiteIndexEntry, { isLoading: isAddingWebsiteIndexEntry }] = useAddWebsiteIndexEntryMutation();
    const [addSitemapImportProcess, { isLoading: isAddingSitemapImportProcess }] = useAddSitemapImportProcessMutation();
    const [addDomainAnalysisProcess, { isLoading: isAddingDomainAnalysisProcess }] = useAddDomainAnalysisProcessMutation();
    const [createBackgroundAnalysis] = useCreateBackgroundAnalysisReportMutation();
    const { dpMaxDomains } = useGetSettingsForPlan();
    const { userSubscription } = useGetSubscription();


    const planDetails = userSubscription?.plan_details;
    const userPlanCode = planDetails ? planDetails.code : '';
    const existingWebsiteDomains = websiteIndexEntries ? websiteIndexEntries.map((entry) => entry.domain) : [];

    const handleDomainChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputDomainError('');
        setShowMaxDomainsError(false);
        setInputDomain(event.target.value);
    };

    const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const { domain, homepage, error } = extractDomainAndHomepage(inputDomain);

        if (error) {
            setInputDomainError(error);
            return;
        }

        if (domain && existingWebsiteDomains.includes(domain)) {
            setInputDomainError('This domain is already added!');
            return;
        }

        if (!websiteIndexEntries || !dpMaxDomains) {
            return;
        }

        if (websiteIndexEntries.length >= dpMaxDomains) {
            setShowMaxDomainsError(true);
            return;
        }

        let newWebsiteIndexEntry;

        if (domain && homepage) {
            try {
                const settings = {
                    location_name: 'United States',
                    language_code: 'en',
                    competitors: [],
                    regions: [],
                };

                newWebsiteIndexEntry = await addWebsiteIndexEntry({ domain: domain, homepage_url: homepage, settings }).unwrap();
            } catch (error) {
                setInputDomainError(`Cannot access domain ${domain}!`);
                notification.error({
                    message: `We cannot access ${domain} in order to perform our test!`,
                    description: 'Either the domain is not online, or our tool is being blocked by your server.',
                });
                return;
            }
        }

        if (newWebsiteIndexEntry) {
            try {
                if (newWebsiteIndexEntry.sitemap_urls.length > 0) {
                    await addSitemapImportProcess({ w_guid: newWebsiteIndexEntry.guid, sitemap_urls: newWebsiteIndexEntry.sitemap_urls }).unwrap();
                }

                await addDomainAnalysisProcess({ w_guid: newWebsiteIndexEntry.guid }).unwrap();

                // start a full legacy seo-audit                    
                await createBackgroundAnalysis({ url: newWebsiteIndexEntry.homepage_url, competitors: [], source: 'add_tracked_website_wizard' }).unwrap();

                onFinish(newWebsiteIndexEntry.guid);
            } catch (_) {
                notification.error({ message: 'Something went wrong!', description: 'Please try again or contact support!' });
            }
        }
    };

    const hasProfessionalPlan = userPlanCode.includes('professional');
    const isButtonLoading = isAddingWebsiteIndexEntry || isAddingDomainAnalysisProcess || isAddingSitemapImportProcess;

    return (
        <>
            <Card className="deep-analysis-add-website-card">
                <Form onSubmit={handleFormSubmit}>
                    <Form.Item
                        validateStatus={inputDomainError ? 'error' : 'validating'}
                        help={inputDomainError ? inputDomainError : ''}
                    >
                        <div className="add-website-input-group">
                            <Input
                                className="website-input"
                                autoCorrect="off"
                                autoCapitalize="off"
                                prefix={
                                    <Icon
                                        type="plus"
                                        style={{ color: 'rgba(0,0,0,.25)', fontSize: '20px' }}
                                    />
                                }
                                disabled={isLoadingWebsiteIndexEntries}
                                placeholder="Add a new domain"
                                name="url"
                                size="large"
                                value={inputDomain}
                                onChange={handleDomainChange}
                            />

                            <Button
                                className="add-website-button"
                                type="primary"
                                htmlType="submit"
                                size="large"
                                disabled={isLoadingWebsiteIndexEntries}
                                loading={isButtonLoading}
                            >
                                Add Domain
                            </Button>
                        </div>
                    </Form.Item>
                </Form>
            </Card>

            {showMaxDomainsError && (
                <Alert
                    className="deep-analysis-website-form-alert"
                    type="error"
                    showIcon
                    message={
                        <>
                            Looks like you've hit your domain limit!{' '}
                            {hasProfessionalPlan ? (
                                <>
                                    <a href="mailto:information@seositecheckup.com">Contact us</a> if you require a higher domain limit
                                </>
                            ) : (
                                <>
                                    Want to add more? Just <b onClick={() => navigate('/subscription/plans')}>upgrade to a bigger</b> plan to unlock more
                                    domains
                                </>
                            )}
                        </>
                    }
                />
            )}
        </>
    );
}
